import type { TranslationKey } from '../../i18n/he';
import type { ArmorName, WeaponName } from '../combat';

/*
 * הגדרות יחידות.
 *
 * יחידה חדשה = רשומה אחת כאן. המנוע מפעיל לפי `UnitTypeId` (אינדקס בטבלה) ואינו
 * מכיר אף יחידה בשמה.
 *
 * `sprite` הוא **רמז ציור בלבד ולעולם לא לוגיקה** — הציור עצמו חי ב-render/atlas.ts,
 * כדי שתיקיית התוכן תישאר דאטה טהורה בלי תלות בקנבס.
 */

export type SpriteKind = 'tank' | 'infantry' | 'harvester' | 'gunship' | 'atInfantry' | 'hound';

/**
 * שכבת התנועה.
 *
 * **אוויר אינו "יחידה חזקה" — הוא ציר שני.** יחידת אוויר מתעלמת מהטרשת ומהנתיבים,
 * ואי אפשר לפגוע בה בנשק שאינו מצהיר `targets: ['air']`. מכאן ששאלת האיזון היא
 * "כמה נ"מ בניתי" ולא "כמה טנקים" — וזה בדיוק מה שהופך אותה להחלטה.
 */
export type UnitLayer = 'ground' | 'air';

export interface UnitDef {
    readonly id: string;
    readonly nameKey: TranslationKey;
    /** משפט אחד: מה היחידה עושה ומול מה היא טובה. ראו ההערה ב-buildings. */
    readonly descKey: TranslationKey;
    readonly hp: number;
    /** יחידות עולם לטיק. TILE=256, כלומר 26 הוא בערך אריח לחצי שנייה. */
    readonly speed: number;
    /** רדיוס ביחידות עולם — לבחירה, לציור ובהמשך להתנגשות. */
    readonly radius: number;
    readonly cost: number;
    readonly sprite: SpriteKind;
    /** קרקע או אוויר. ראו `UnitLayer`. */
    readonly layer: UnitLayer;
    /** מול איזה סוג שריון תוקפים אותה. ראו content/combat. */
    readonly armor: ArmorName;
    /** הנשק שלה, או null ליחידה לא לוחמת (קוצר, מהנדס). */
    readonly weapon: WeaponName | null;
    /** טווח שבו יחידה רוכשת מטרה מיוזמתה. 0 = רק בפקודה מפורשת. */
    readonly sightRange: number;
    /** קיבולת עפרות. 0 = אינה קוצרת. */
    readonly capacity: number;
    /** כמה עפרות נכרות בטיק. */
    readonly harvestRate: number;
    /** המבנה שמייצר אותה. זה עץ הטכנולוגיה: אין קסרקטין — אין חי"ר. */
    readonly producedBy: string;
}

export const UNIT_DEFS = [
    {
        id: 'gi',
        nameKey: 'unit_gi',
        descKey: 'unit_gi_desc',
        hp: 125,
        speed: 14,
        radius: 76,
        cost: 200,
        sprite: 'infantry',
        layer: 'ground',
        armor: 'flesh',
        weapon: 'rifle',
        sightRange: 5 * 256,
        capacity: 0,
        harvestRate: 0,
        producedBy: 'barracks',
    },
    {
        id: 'grizzly',
        nameKey: 'unit_grizzly',
        descKey: 'unit_grizzly_desc',
        hp: 300,
        speed: 26,
        radius: 110,
        cost: 700,
        sprite: 'tank',
        layer: 'ground',
        armor: 'heavy',
        weapon: 'tankCannon',
        sightRange: 6 * 256,
        capacity: 0,
        harvestRate: 0,
        producedBy: 'factory',
    },
    {
        id: 'harvester',
        nameKey: 'unit_harvester',
        descKey: 'unit_harvester_desc',
        hp: 600,
        speed: 18,
        radius: 105,
        cost: 1400,
        sprite: 'harvester',
        layer: 'ground',
        // שריון קל: שווה להגן עליו, אבל הוא לא טנק.
        armor: 'light',
        weapon: null,
        sightRange: 0,
        capacity: 700,
        harvestRate: 12,
        producedBy: 'factory',
    },
    /*
     * **בסוף המערך, תמיד.** האינדקס הוא המזהה שנשמר בתוך קובצי השמירה וב-hash;
     * הזזה של רשומה טוענת כל שמירה קיימת עם היחידות הלא נכונות, בשקט.
     * ‏`content/production.test.ts` מקבע את זה ויפיל כל שינוי סדר.
     */
    {
        id: 'gunship',
        nameKey: 'unit_gunship',
        descKey: 'unit_gunship_desc',
        hp: 220,
        // מהיר יותר מכל דבר בקרקע — הוא לא עוקף מכשולים, הוא מתעלם מהם.
        speed: 38,
        radius: 100,
        cost: 1200,
        sprite: 'gunship',
        layer: 'air',
        armor: 'air',
        weapon: 'gunshipRockets',
        sightRange: 7 * 256,
        capacity: 0,
        harvestRate: 0,
        producedBy: 'airfield',
    },
    /*
     * צוות נ"ט.
     *
     * **החור שהוא סוגר הוא החלטה, לא כוח אש.** עד עכשיו היו שתי יחידות לוחמות
     * בלבד — חי"ר נגד חי"ר וטנק נגד הכול — ולכן "מה לייצר" לא הייתה שאלה, והקסרקטין
     * הפך למיותר ברגע שהיה מפעל. הצוות הזה הופך את **מה שהאויב בונה** למידע ששווה
     * להשיג: מול צבא טנקים הוא הקנייה הטובה במשחק, ומול צבא חי"ר הוא בזבוז.
     *
     * זול ושברירי בכוונה: הוא מנצח בהחלפה, לא בעמידות.
     */
    {
        id: 'bazooka',
        nameKey: 'unit_bazooka',
        descKey: 'unit_bazooka_desc',
        // פחות מחייל: הוא נושא משגר, לא שריון.
        hp: 100,
        speed: 12,
        radius: 76,
        cost: 300,
        sprite: 'atInfantry',
        layer: 'ground',
        armor: 'flesh',
        weapon: 'atLauncher',
        // אריח אחד מעבר לטווח הנשק. ראו ההערה על הרצועה ב-`atLauncher`.
        sightRange: 6 * 256,
        capacity: 0,
        harvestRate: 0,
        producedBy: 'barracks',
    },
    /*
     * כלב סיור — **עיניים, לא נשק.**
     *
     * מאז שיש ערפל קרב, "מה האויב בונה" הוא מידע שצריך ללכת ולהביא. הכלב יוצא
     * מהקסרקטין ולא מהמפעל בכוונה: המפעל דורש מפעל עיבוד ומפעל רק"ם (4,000 אשראי),
     * ועד שהוא עומד השחקן כבר בחר את הצבא שלו. מידע שמגיע אחרי ההחלטה אינו מידע.
     * הקסרקטין עולה 500 ודורש רק חשמל — כלומר הסיור זמין בדיוק בחלון שבו הוא
     * עדיין יכול לשנות את מה שנבנה.
     *
     * **`weapon: null` הוא החלטת עיצוב ולא ויתור.** נשק כלשהו היה גורר אותו למערכת
     * רכישת המטרות, ושם יחידה שרואה 11 אריחים ויורה למרחק אריח הייתה רודפת עד גבול
     * הרצועה (4 אריחים), מוותרת, חוזרת ורוכשת מחדש — לולאה שנראית בדיוק כמו באג.
     * חסר נשק, הוא נדלג בלולאת הקרב לגמרי, בדיוק כמו הקוצר.
     */
    {
        id: 'hound',
        nameKey: 'unit_hound',
        descKey: 'unit_hound_desc',
        // ירייה אחת של רובה (18 × 2 במטח) לוקחת ממנו חצי. זה המחיר של הראייה.
        hp: 60,
        // מהיר מטנק ואיטי ממסוק: הוא בורח מקרקע, לא מאוויר.
        speed: 34,
        radius: 60,
        cost: 150,
        sprite: 'hound',
        layer: 'ground',
        armor: 'flesh',
        weapon: null,
        /*
         * 11 אריחים מול 6 של הטנק. הפער חייב להיות גדול כי `MIN_SIGHT` ב-`sim/vision`
         * נותן 4 אריחים לכל יחידה בחינם — יחידת סיור עם 7 אריחים הייתה מרגישה
         * כמו טנק, ולא כמו סיור.
         */
        sightRange: 11 * 256,
        capacity: 0,
        harvestRate: 0,
        producedBy: 'barracks',
    },
] as const satisfies readonly UnitDef[];

export type UnitTypeName = (typeof UNIT_DEFS)[number]['id'];

/** אינדקס בתוך UNIT_DEFS. זה מה שנשמר במצב המשחק ונכנס ל-hash. */
export type UnitTypeId = number;

export const UNIT_BY_NAME: Readonly<Record<UnitTypeName, UnitTypeId>> = Object.fromEntries(
    UNIT_DEFS.map((def, index) => [def.id, index]),
) as Record<UnitTypeName, UnitTypeId>;

export function unitDef(id: UnitTypeId): UnitDef {
    const def = UNIT_DEFS[id];
    if (!def) throw new Error(`סוג יחידה לא מוכר: ${id}`);
    return def;
}
